<template>
  <div class="opc-cart__currency" v-if="$context.is.switchCurrency || currencyOptions.length">
    <div class="opc-cart__currency__icon">
      <span :class="`opc-cny-icon opc-cny-icon--${currency}`"></span>
    </div>
    
    <div v-if="$context.is.switchCurrency" class="opc-cart__currency__display">
      <span class="opc-cart__currency__option__inner">{{ currency }}</span>
    </div>
    
    <zm-select v-else data-testid="checkout-select-395" v-model="currency" @change="(currency) => $tracking.orderSummary(currency)" label="currency" popper-append-to-body>
      <zm-option
        v-for="(currency, index) in currencyOptions"
        data-testid="checkout-option-396"
        :key="index"
        :value="currency.value"
        class="opc-cart__currency__option"
      >
        <span :class="`opc-cny-icon opc-cny-icon--${currency.value}`"></span>
        <span class="opc-cart__currency__option__inner">{{ currency.value }}</span>
      </zm-option>
    </zm-select>
  </div>
</template>

<script>
import { CURRENCY_COOKIE_KEY } from '@/utils/currency';
import { mapGetters } from 'vuex';
import { getQueryByLocationSearch } from '@/utils/index';
import _ from 'lodash';
import { isIAPRedirectUser, IAP_CURRENCY_WHITE_LIST } from '@/utils/iap'

export default {
  data() {
    return {
      currency: '',
      currencyOptions: [],
    };
  },
  computed: {
    ...mapGetters(['disableCurrency4PlanCategory', 'getSelectedPlans']),
    disabledCurrencyList() {
      const parentCategoryList = this.getSelectedPlans.map(plan => plan?.parent_category);

      return _.chain(parentCategoryList)
        .reduce((pre, parentCategoryCode) => {
        const currencyList = this.disableCurrency4PlanCategory?.[parentCategoryCode] || [];
        return _.concat(pre, currencyList);
      }, [])
      .uniq()
      .value();
    },
  },
  watch: {
    currency(newVal, oldVal) {
      if (newVal && oldVal) {
        let domain = document.body.getAttribute('data-cd') || '.zoom.us';
        this.$cookie.set(CURRENCY_COOKIE_KEY, newVal, { expires: 1, path: '/', domain, secure: true });
        window.location.reload();
      }
    },
  },
  methods: {
    init() {
      const targetCurrency = getQueryByLocationSearch().currency;
      const currency = this.$context.is.switchCurrency ? targetCurrency : this.$cookie.get(CURRENCY_COOKIE_KEY);
      this.$set(this, 'currency', currency);
      const callback = () => {
        // don't depend on doms
        // const currencyOptions = [
        //   ...document.querySelectorAll(`.dropdown-currency .dropdown-menu *[data-currency]`),
        // ].map((x) => ({
        //   value: x.getAttribute('data-currency'),
        //   label: x.innerText,
        // }));
        let currencyOptions = Object.keys(window.i18nCurrencyMap || {}).map((x) => ({
          value: x,
          label: x,
        })).filter(item => {
          return !_.includes(this.disabledCurrencyList, item.value);
        });

        // Restrict the currency list on the OPC page to USD only during IAP redirect flow.
        // Starting in June 2025, users coming through the in-app redirection flow (detected via isIAPRedirectUser)
        // are allowed only if their country is the United States, due to Apple policy restrictions.
        // Therefore, only USD is supported in this flow.
        if (isIAPRedirectUser) {
          const allowList = _.uniq(IAP_CURRENCY_WHITE_LIST.concat(this.currency))
          if (currencyOptions.some(c => allowList.includes(c.value))) {
            currencyOptions = currencyOptions.filter(c => allowList.includes(c.value))
          }
        }
        this.$set(this, 'currencyOptions', currencyOptions);
      };
      callback();
      // window.addEventListener('DOMContentLoaded', callback);
    },
  },
  created() {
    this.init();
  },
};
</script>

<style lang="scss">
$icon-size: 16px;
.opc-cart__currency {
  margin-left: auto;
  display: flex;
  align-items: center;
  &__icon {
    position: relative;
    width: $icon-size;
    height: $icon-size;
    left: $icon-size + 8px;
  }
  &__option {
    &__inner {
      padding-left: 24px;
    }
  }

  .zm-select {
    border: none;
    background: transparent;
    cursor: pointer;
    &-span__inner {
      min-width: auto !important;
    }
    .zm-select-input {
      text-align: right;
      padding-right: 16px;
      padding-left: 8px + $icon-size + 8px;
      .zm-select-toggle {
        right: 0px;
      }
      &__inner {
        font-size: 12px;
        line-height: 18px;
        color: #515079;
      }
    }
  }
}

.opc-cny-icon {
  position: absolute;
  height: $icon-size * 2;
  width: $icon-size * 2;
  border: 1px solid rgba($color: #666, $alpha: 0.2);
  box-sizing: content-box;
  border-radius: $icon-size * 2;
  background-repeat: no-repeat;
  vertical-align: middle;
  background: url('~@/assets/flags-sprite.png');
  transform: scale(0.5) translate(-50%, -50%);
  background-position: 30px 40px;

  &--USD {
    background-position: -392px -296px;
  }

  &--CAD {
    background-position: -344px -8px;
  }

  &--AUD {
    background-position: -56px -8px;
  }

  &--JPY {
    background-position: -56px -152px;
  }

  &--INR {
    background-position: -296px -104px;
  }

  &--EUR {
    background-position: -440px -632px;
  }

  &--GBP {
    background-position: -344px -296px;
  }

  &--NZD {
    background-position: -56px -200px;
  }

  &--VND {
    background-position: -8px -344px;
  }

  &--MXN {
    background-position: -440px -152px;
  }

  &--SGD {
    background-position: -200px -248px;
  }

  &--BRL {
    background-position: -248px -8px;
  }

  &--IDR {
    background-position: 136px -104px;
  }

  &--KRW {
    background-position: 88px -248px;
  }

  &--PHP {
    background-position: 184px -200px;
  }

  &--MYR {
    background-position: 136px -152px;
  }

  &--SEK {
    background-position: -8px -296px;
  }

  &--PLN {
    background-position: -344px -200px;
  }

  &--NOK {
    background-position: -104px -200px;
  }

  &--DKK {
    background-position: -200px -56px;
  }

  &--HKD {
    background-position: -200px -104px;
  }

  &--CHF {
    background-position: -56px -296px;
  }

  &--SAR {
    background-position: -150px -248px;
  }

  &--ZAR {
    background-position: -344px -248px;
  }

  &--AED {
    background-position: -296px -296px;
  }
}

.opc-cart__currency__display {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 4px 11px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.88);
  line-height: 1.5714285714285714;
  
  .opc-cart__currency__option__inner {
    color: rgba(0, 0, 0, 0.88);
  }
}
</style>
